import { useState, useEffect, useRef } from "react";
import { 
  ScrollText, 
  RefreshCw, 
  Trash2, 
  Filter,
  Download,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../services/api";
import { toast } from "sonner";

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState("all");
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logContainerRef = useRef(null);

  const fetchLogs = async () => {
    try {
      const params = filterLevel !== "all" ? { level: filterLevel } : {};
      const res = await api.get("/logs", { params: { ...params, limit: 200 } });
      setLogs(res.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 5000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [filterLevel, autoRefresh]);

  const handleClearLogs = async () => {
    try {
      const res = await api.delete("/logs", { params: { older_than_days: 0 } });
      toast.success(`${res.data.deleted_count} logs removidos`);
      setClearDialogOpen(false);
      fetchLogs();
    } catch (error) {
      toast.error("Erro ao limpar logs");
    }
  };

  const handleExport = () => {
    const content = logs.map(log => 
      `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}${log.flow_name ? ` (${log.flow_name})` : ''}`
    ).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gateway-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Logs exportados");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("pt-BR", { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error': return 'log-level-error';
      case 'warning': return 'log-level-warning';
      case 'success': return 'log-level-success';
      default: return 'log-level-info';
    }
  };

  const getLevelBadge = (level) => {
    switch (level) {
      case 'error': return 'bg-red-500/20 text-red-400';
      case 'warning': return 'bg-yellow-500/20 text-yellow-400';
      case 'success': return 'bg-green-500/20 text-green-400';
      default: return 'bg-blue-500/20 text-blue-400';
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Logs do Sistema</h1>
          <p className="text-sm text-zinc-500">Histórico de transformações e eventos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" : ""}
            data-testid="auto-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
            data-testid="export-logs-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="text-red-400 hover:text-red-300 hover:border-red-400"
            onClick={() => setClearDialogOpen(true)}
            data-testid="clear-logs-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-400">Filtrar por nível:</span>
            </div>
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700" data-testid="log-level-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="success">Sucesso</SelectItem>
                <SelectItem value="warning">Aviso</SelectItem>
                <SelectItem value="error">Erro</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto text-sm text-zinc-500">
              {logs.length} logs
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Terminal */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="log-terminal-card">
        <CardHeader className="pb-0">
          <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-cyan-400" />
            Terminal de Logs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div 
            ref={logContainerRef}
            className="log-terminal"
            data-testid="log-terminal"
          >
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : logs.length > 0 ? (
              logs.map((log, index) => (
                <div 
                  key={log.id || index} 
                  className="log-line hover:bg-zinc-800/50 rounded px-2 py-1 transition-colors"
                >
                  <span className="log-timestamp">[{formatDate(log.timestamp)}]</span>
                  {' '}
                  <span className={getLevelColor(log.level)}>
                    [{log.level.toUpperCase().padEnd(7)}]
                  </span>
                  {' '}
                  <span className="text-zinc-300">{log.message}</span>
                  {log.flow_name && (
                    <span className="text-cyan-400/70 ml-2">({log.flow_name})</span>
                  )}
                  {log.duration_ms && (
                    <span className="text-zinc-600 ml-2">[{log.duration_ms}ms]</span>
                  )}
                </div>
              ))
            ) : (
              <div className="text-zinc-500 text-center py-8">
                Nenhum log encontrado
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Log View */}
      {logs.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="log-details-card">
          <CardHeader>
            <CardTitle className="text-lg font-mono text-white">Detalhes dos Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Timestamp</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Nível</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Mensagem</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-medium">Fluxo</th>
                    <th className="text-right py-3 px-4 text-zinc-500 font-medium">Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.slice(0, 50).map((log) => (
                    <tr 
                      key={log.id} 
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Clock className="w-3 h-3" />
                          <span className="font-mono text-xs">{formatFullDate(log.timestamp)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getLevelBadge(log.level)}>
                          {log.level}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-zinc-300 max-w-md truncate">
                        {log.message}
                      </td>
                      <td className="py-3 px-4">
                        {log.flow_name ? (
                          <Badge variant="outline" className="text-xs">{log.flow_name}</Badge>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right text-zinc-500 font-mono text-xs">
                        {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clear Confirmation */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar Todos os Logs</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover todos os logs? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearLogs}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-clear-logs-btn"
            >
              Limpar Todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
